import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const SystemConfigSection = () => {
  const [config, setConfig] = useState({
    notifications: {
      emailAlerts: true,
      pushNotifications: true,
      smsAlerts: false,
      alertFrequency: 'immediate'
    },
    audit: {
      enableLogging: true,
      logRetention: 90,
      exportFormat: 'csv',
      autoBackup: true
    },
    security: {
      sessionTimeout: 30,
      requireTwoFactor: true,
      passwordExpiry: 90,
      encryptionLevel: 'quantum'
    },
    system: {
      maintenanceMode: false,
      debugMode: false,
      apiRateLimit: 1000,
      maxConcurrentUsers: 50
    }
  });

  const [hasChanges, setHasChanges] = useState(false);
  const [saving, setSaving] = useState(false);

  const alertFrequencyOptions = [
    { value: 'immediate', label: 'Immediate', description: 'Send alerts as they occur' },
    { value: 'hourly', label: 'Hourly Digest', description: 'Batch alerts every hour' },
    { value: 'daily', label: 'Daily Summary', description: 'Send daily summary report' }
  ];

  const exportFormatOptions = [
    { value: 'csv', label: 'CSV Format' },
    { value: 'json', label: 'JSON Format' },
    { value: 'pdf', label: 'PDF Report' }
  ];

  const encryptionLevelOptions = [
    { value: 'standard', label: 'Standard AES-256' },
    { value: 'enhanced', label: 'Enhanced RSA-4096' },
    { value: 'quantum', label: 'Quantum-Resistant' }
  ];

  const handleConfigChange = (section, field, value) => {
    setConfig(prev => ({
      ...prev,
      [section]: {
        ...prev?.[section],
        [field]: value
      }
    }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    setSaving(true);
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    setSaving(false);
    setHasChanges(false);
    
    console.log('System configuration saved successfully');
  };

  const handleExportConfig = () => {
    const configData = JSON.stringify(config, null, 2);
    const blob = new Blob([configData], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `securelock-config-${new Date()?.toISOString()?.split('T')?.[0]}.json`;
    document.body?.appendChild(a);
    a?.click();
    document.body?.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="bg-card rounded-lg border border-border p-6 security-shadow-card">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-foreground flex items-center space-x-2">
            <Icon name="Settings" size={20} className="text-primary" />
            <span>System Configuration</span>
          </h3>
          <p className="text-sm text-muted-foreground mt-1">
            Configure system-wide settings, notifications, and security parameters
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            size="sm"
            iconName="Download"
            iconPosition="left"
            onClick={handleExportConfig}
          >
            Export Config
          </Button>
          {hasChanges && (
            <Button
              variant="default"
              size="sm"
              loading={saving}
              iconName="Save"
              iconPosition="left"
              onClick={handleSave}
            >
              Save Changes
            </Button>
          )}
        </div>
      </div>
      <div className="space-y-8">
        {/* Notification Settings */}
        <div className="space-y-4">
          <div>
            <h4 className="text-md font-medium text-foreground flex items-center space-x-2">
              <Icon name="Bell" size={16} className="text-accent" />
              <span>Notification Preferences</span>
            </h4>
            <p className="text-sm text-muted-foreground">Configure how and when to receive alerts</p>
          </div>
          
          <div className="space-y-4 p-4 bg-muted/30 rounded-lg border border-border/50">
            <div className="space-y-3">
              <Checkbox
                label="Email alerts"
                description="Receive security alerts via email"
                checked={config?.notifications?.emailAlerts}
                onChange={(e) => handleConfigChange('notifications', 'emailAlerts', e?.target?.checked)}
              />
              <Checkbox
                label="Push notifications"
                description="Browser and mobile push notifications"
                checked={config?.notifications?.pushNotifications}
                onChange={(e) => handleConfigChange('notifications', 'pushNotifications', e?.target?.checked)}
              />
              <Checkbox
                label="SMS alerts"
                description="Critical alerts via SMS (additional charges apply)"
                checked={config?.notifications?.smsAlerts}
                onChange={(e) => handleConfigChange('notifications', 'smsAlerts', e?.target?.checked)}
              />
            </div>
            
            <Select
              label="Alert Frequency"
              options={alertFrequencyOptions}
              value={config?.notifications?.alertFrequency}
              onChange={(value) => handleConfigChange('notifications', 'alertFrequency', value)}
              className="mt-4"
            />
          </div>
        </div>

        {/* Audit & Logging */}
        <div className="space-y-4">
          <div>
            <h4 className="text-md font-medium text-foreground flex items-center space-x-2">
              <Icon name="FileText" size={16} className="text-success" />
              <span>Audit & Logging</span>
            </h4>
            <p className="text-sm text-muted-foreground">Configure audit trails and data retention</p>
          </div>
          
          <div className="space-y-4 p-4 bg-muted/30 rounded-lg border border-border/50">
            <div className="space-y-3">
              <Checkbox
                label="Enable comprehensive logging"
                description="Log all system activities and user actions"
                checked={config?.audit?.enableLogging}
                onChange={(e) => handleConfigChange('audit', 'enableLogging', e?.target?.checked)}
              />
              <Checkbox
                label="Automatic backup"
                description="Daily backup of audit logs to secure storage"
                checked={config?.audit?.autoBackup}
                onChange={(e) => handleConfigChange('audit', 'autoBackup', e?.target?.checked)}
              />
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Log Retention (days)"
                type="number"
                min="30"
                max="365"
                value={config?.audit?.logRetention}
                onChange={(e) => handleConfigChange('audit', 'logRetention', parseInt(e?.target?.value))}
                description="How long to keep audit logs"
              />
              <Select
                label="Export Format"
                options={exportFormatOptions}
                value={config?.audit?.exportFormat}
                onChange={(value) => handleConfigChange('audit', 'exportFormat', value)}
              />
            </div>
          </div>
        </div>

        {/* Security Settings */}
        <div className="space-y-4">
          <div>
            <h4 className="text-md font-medium text-foreground flex items-center space-x-2">
              <Icon name="Shield" size={16} className="text-warning" />
              <span>Security Configuration</span>
            </h4>
            <p className="text-sm text-muted-foreground">Advanced security and encryption settings</p>
          </div>
          
          <div className="space-y-4 p-4 bg-muted/30 rounded-lg border border-border/50">
            <Checkbox
              label="Require two-factor authentication"
              description="Mandatory 2FA for all administrator accounts"
              checked={config?.security?.requireTwoFactor}
              onChange={(e) => handleConfigChange('security', 'requireTwoFactor', e?.target?.checked)}
            />
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Input
                label="Session Timeout (minutes)"
                type="number"
                min="5"
                max="120"
                value={config?.security?.sessionTimeout}
                onChange={(e) => handleConfigChange('security', 'sessionTimeout', parseInt(e?.target?.value))}
                description="Auto-logout after inactivity"
              />
              <Input
                label="Password Expiry (days)"
                type="number"
                min="30"
                max="365"
                value={config?.security?.passwordExpiry}
                onChange={(e) => handleConfigChange('security', 'passwordExpiry', parseInt(e?.target?.value))}
                description="Force password change interval"
              />
              <Select
                label="Encryption Level"
                options={encryptionLevelOptions}
                value={config?.security?.encryptionLevel}
                onChange={(value) => handleConfigChange('security', 'encryptionLevel', value)}
              />
            </div>
          </div>
        </div>

        {/* System Performance */}
        <div className="space-y-4">
          <div>
            <h4 className="text-md font-medium text-foreground flex items-center space-x-2">
              <Icon name="Cpu" size={16} className="text-error" />
              <span>System Performance</span>
            </h4>
            <p className="text-sm text-muted-foreground">Performance and operational limits</p>
          </div>
          
          <div className="space-y-4 p-4 bg-muted/30 rounded-lg border border-border/50">
            <div className="space-y-3">
              <Checkbox
                label="Maintenance mode"
                description="Enable maintenance mode (blocks user access)"
                checked={config?.system?.maintenanceMode}
                onChange={(e) => handleConfigChange('system', 'maintenanceMode', e?.target?.checked)}
              />
              <Checkbox
                label="Debug mode"
                description="Enable detailed logging for troubleshooting"
                checked={config?.system?.debugMode}
                onChange={(e) => handleConfigChange('system', 'debugMode', e?.target?.checked)}
              />
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="API Rate Limit (requests/hour)"
                type="number"
                min="100"
                max="10000"
                value={config?.system?.apiRateLimit}
                onChange={(e) => handleConfigChange('system', 'apiRateLimit', parseInt(e?.target?.value))}
                description="Maximum API requests per hour"
              />
              <Input
                label="Max Concurrent Users"
                type="number"
                min="10"
                max="500"
                value={config?.system?.maxConcurrentUsers}
                onChange={(e) => handleConfigChange('system', 'maxConcurrentUsers', parseInt(e?.target?.value))}
                description="Maximum simultaneous users"
              />
            </div>
          </div>
        </div>
      </div>
      {/* Configuration Status */}
      <div className="mt-8 p-4 bg-success/10 rounded-lg border border-success/20">
        <h5 className="text-sm font-medium text-success mb-2 flex items-center space-x-2">
          <Icon name="CheckCircle" size={16} />
          <span>System Status</span>
        </h5>
        <div className="text-xs text-muted-foreground space-y-1">
          <p>• Notifications: {config?.notifications?.emailAlerts ? 'Email enabled' : 'Email disabled'}, {config?.notifications?.pushNotifications ? 'Push enabled' : 'Push disabled'}</p>
          <p>• Security: {config?.security?.encryptionLevel} encryption, {config?.security?.requireTwoFactor ? '2FA required' : '2FA optional'}</p>
          <p>• Audit: {config?.audit?.enableLogging ? 'Logging active' : 'Logging disabled'}, {config?.audit?.logRetention} days retention</p>
          <p>• Performance: {config?.system?.apiRateLimit} req/hr limit, {config?.system?.maxConcurrentUsers} max users</p>
        </div>
      </div>
    </div>
  );
};

export default SystemConfigSection;